import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { ChevronLeft, ChevronRight, Loader2, PartyPopper, Trash2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { FormField } from '@/components/ui/form-field';
import { NativeSelect } from '@/components/ui/native-select';
import { Switch } from '@/components/ui/switch';
import { ernaehrungAPI } from '@/lib/api';

/* Genuss-Planer (11.10.2026, Stufe 1): Wochenkarte (Budget, je Tag Basis/Ausgleich/Ziel/gegessen) und die Liste der
   Genussmahlzeiten — im CRM am Mitglied und auf der Trainer-Webseite. Gerechnet wird nur auf dem Server
   (backend/ernaehrung/genuss.py); hier steht, was er liefert. `quelle` kapselt die Aufrufe, damit dieselbe Karte mit
   dem CRM-Client und mit dem Client der Trainer-Webseite läuft. Bewusst ohne Importe aus ErnaehrungPage (sonst
   kreisen die Importe: die Seite bindet den Schalter aus dieser Datei ein). */

const MAHLZEIT = { fruehstueck: 'Frühstück', mittag: 'Mittagessen', abend: 'Abendessen', snack: 'Snack' };
const PORTION = { klein: 'klein', normal: 'normal', gross: 'groß' };
const STATUS = {
  geplant: { label: 'geplant', cls: 'bg-primary/10 text-primary border-primary/30' },
  gegessen: { label: 'gegessen', cls: 'bg-emerald-500/10 text-emerald-700 border-emerald-200' },
  verworfen: { label: 'verworfen', cls: 'text-muted-foreground' },
};
const TATSAECHLICH = { weniger: 'weniger als geplant', wie_geplant: 'wie geplant', mehr: 'mehr als geplant' };

const kcal = (n) => `${Math.round(n || 0).toLocaleString('de-DE')} kcal`;
const mitVorzeichen = (n) => (n > 0 ? `+${n.toLocaleString('de-DE')}` : n < 0 ? `−${Math.abs(n).toLocaleString('de-DE')}` : '0');
const tagText = (iso, opt = { weekday: 'short', day: '2-digit', month: '2-digit' }) => new Date(`${iso}T12:00:00`).toLocaleDateString('de-DE', opt);
const plusTage = (iso, n) => { const d = new Date(`${iso}T12:00:00`); d.setDate(d.getDate() + n); return d.toISOString().slice(0, 10); };
const fehlertext = (e, rest = 'Das hat nicht geklappt') => { const d = e?.response?.data?.detail; return typeof d === 'string' ? d : rest; };

/* Aufrufe für das CRM (Recht ernaehrung.view zum Lesen, leads.edit zum Einplanen/Löschen/Begrenzen). */
export const genussQuelleCrm = (leadId) => ({
  woche: (datum) => ernaehrungAPI.genussWoche(leadId, datum).then((r) => r.data),
  vorlagen: () => ernaehrungAPI.genussVorlagen().then((r) => r.data.vorlagen),
  vorschau: (daten) => ernaehrungAPI.genussVorschau(leadId, daten).then((r) => r.data),
  anlegen: (daten) => ernaehrungAPI.genussAnlegen(leadId, daten).then((r) => r.data),
  loeschen: (id) => ernaehrungAPI.genussLoeschen(leadId, id).then((r) => r.data),
  grenze: (n) => ernaehrungAPI.genussGrenze(leadId, n).then((r) => r.data),
});

/* Aufrufe für die Trainer-Webseite (`coach` = axios-Client mit dem Trainer-Token). */
export const genussQuelleCoach = (coach, mid) => ({
  woche: (datum) => coach.get(`/ernaehrung/mitglied/${mid}/woche`, { params: datum ? { datum } : {} }).then((r) => r.data),
  vorlagen: () => coach.get('/ernaehrung/genuss/vorlagen').then((r) => r.data.vorlagen),
  vorschau: (daten) => coach.post(`/ernaehrung/mitglied/${mid}/genuss/vorschau`, daten).then((r) => r.data),
  anlegen: (daten) => coach.post(`/ernaehrung/mitglied/${mid}/genuss`, daten).then((r) => r.data),
  loeschen: (id) => coach.delete(`/ernaehrung/mitglied/${mid}/genuss/${id}`).then((r) => r.data),
  grenze: (n) => coach.put(`/ernaehrung/mitglied/${mid}/genuss-grenze`, { max_je_woche: n }).then((r) => r.data),
});

function Budget({ w }) {
  const teile = [['gegessen', w.gegessen_kcal, 'bg-primary'], ['geplant', w.geplant_kcal, 'bg-primary/40'], ['frei', w.frei_kcal, 'bg-muted-foreground/20']];
  const summe = Math.max(1, teile.reduce((s, [, n]) => s + n, 0));
  return (
    <div className="space-y-1.5">
      <p className="text-sm">Wochenbudget <strong className="tabular-nums">{kcal(w.budget_kcal)}</strong></p>
      <div className="flex h-2.5 overflow-hidden rounded-full bg-muted" aria-hidden="true">
        {teile.map(([name, n, cls]) => (n > 0 ? <div key={name} className={cls} style={{ width: `${(n / summe) * 100}%` }} /> : null))}
      </div>
      {/* Zahlen stehen immer dabei — der Balken allein sagt niemandem etwas, der ihn nicht sieht */}
      <ul className="flex flex-wrap gap-x-4 gap-y-0.5 text-xs text-muted-foreground">
        {teile.map(([name, n, cls]) => <li key={name} className="inline-flex items-center gap-1.5"><span className={`h-2 w-2 rounded-full ${cls}`} aria-hidden="true" />{name} <span className="tabular-nums text-foreground">{kcal(n)}</span></li>)}
      </ul>
    </div>
  );
}

function Tage({ w, heute }) {
  return (
    <div className="overflow-x-auto rounded-md border">
      <table className="w-full min-w-[34rem] text-sm">
        <caption className="sr-only">Woche: je Tag Basis, Ausgleich, Ziel und gegessen</caption>
        <thead>
          <tr className="bg-muted/50 text-left">
            <th scope="col" className="p-2 font-medium">Tag</th>
            <th scope="col" className="p-2 font-medium text-right">Basis</th>
            <th scope="col" className="p-2 font-medium text-right">Ausgleich</th>
            <th scope="col" className="p-2 font-medium text-right">Ziel</th>
            <th scope="col" className="p-2 font-medium text-right">gegessen</th>
          </tr>
        </thead>
        <tbody>
          {w.tage.map((t) => {
            const genuss = w.genuss.filter((g) => t.genuss_ids.includes(g.id));
            return (
              <tr key={t.datum} className={`border-t align-top ${t.datum === heute ? 'bg-primary/5' : ''}`}>
                <th scope="row" className="p-2 text-left font-normal">
                  {tagText(t.datum)}{t.datum === heute && <span className="ml-1 text-xs text-primary">heute</span>}
                  {genuss.map((g) => <span key={g.id} className="block text-xs">{g.symbol ? `${g.symbol} ` : ''}{g.name}</span>)}
                  {t.hinweis && <span className="block text-xs text-muted-foreground">{t.hinweis}</span>}
                </th>
                <td className="p-2 text-right tabular-nums">{t.basis_kcal.toLocaleString('de-DE')}</td>
                <td className="p-2 text-right tabular-nums">{t.ausgleich_kcal ? mitVorzeichen(t.ausgleich_kcal) : '–'}</td>
                <td className="p-2 text-right tabular-nums font-medium">{t.ziel_kcal.toLocaleString('de-DE')}</td>
                <td className="p-2 text-right tabular-nums">{t.gegessen_kcal ? t.gegessen_kcal.toLocaleString('de-DE') : '–'}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function Einplanen({ quelle, d, onFertig }) {
  const [vorlagen, setVorlagen] = useState([]);
  const leer = { datum: d.heute, mahlzeit: 'abend', vorlage_id: 'pizza', portion: 'normal', name: '', kcal: '', zwei_wochen: false };
  const [f, setF] = useState(leer);
  const [v, setV] = useState(null);
  const [busy, setBusy] = useState('');
  useEffect(() => { quelle.vorlagen().then(setVorlagen).catch(() => {}); }, [quelle]);
  const setze = (teil) => { setF((alt) => ({ ...alt, ...teil })); setV(null); };       // jede Änderung braucht eine neue Vorschau
  const daten = (x) => (x.vorlage_id
    ? { datum: x.datum, mahlzeit: x.mahlzeit, vorlage_id: x.vorlage_id, portion: x.portion, ...(x.zwei_wochen ? { zwei_wochen: true } : {}) }
    : { datum: x.datum, mahlzeit: x.mahlzeit, name: x.name.trim(), kcal: Number(String(x.kcal).replace(',', '.')) || 0, ...(x.zwei_wochen ? { zwei_wochen: true } : {}) });
  const vorschau = async (x = f) => {
    setBusy('vorschau');
    try { setV(await quelle.vorschau(daten(x))); } catch (e) { setV(null); toast.error(fehlertext(e)); } finally { setBusy(''); }
  };
  const vorschlag = (s) => { const neu = { ...f, ...(s.portion ? { portion: s.portion } : {}), ...(s.zwei_wochen ? { zwei_wochen: true } : {}) }; setF(neu); vorschau(neu); };
  const einplanen = async () => {
    setBusy('anlegen');
    try { await quelle.anlegen(daten(f)); toast.success('Eingeplant — Woche und Plan sind angepasst'); setF(leer); setV(null); onFertig(); }
    catch (e) { toast.error(fehlertext(e)); } finally { setBusy(''); }
  };
  const vorlage = vorlagen.find((x) => x.id === f.vorlage_id);
  return (
    <div className="rounded-md border p-3 space-y-2">
      <p className="text-sm font-medium">Genuss einplanen</p>
      <div className="flex flex-wrap items-end gap-2">
        <FormField label="Was?"><NativeSelect className="w-52 max-w-full" value={f.vorlage_id} onChange={(x) => setze({ vorlage_id: x })}
          options={[...vorlagen.map((x) => ({ value: x.id, label: `${x.symbol} ${x.name}` })), { value: '', label: 'Etwas anderes' }]} /></FormField>
        {f.vorlage_id ? (
          <FormField label="Portion"><NativeSelect className="w-40" value={f.portion} onChange={(x) => setze({ portion: x })}
            options={Object.keys(PORTION).map((p) => ({ value: p, label: `${PORTION[p]}${vorlage ? ` · ${kcal(vorlage.portionen[p])}` : ''}` }))} /></FormField>
        ) : (
          <>
            <FormField label="Name"><Input className="w-44" maxLength={80} value={f.name} onChange={(e) => setze({ name: e.target.value })} placeholder="z. B. Omas Geburtstag" /></FormField>
            <FormField label="kcal (geschätzt)"><Input className="w-28" inputMode="numeric" value={f.kcal} onChange={(e) => setze({ kcal: e.target.value })} /></FormField>
          </>
        )}
        <FormField label="Wann?"><Input type="date" className="w-40" min={d.heute} max={plusTage(d.heute, d.voraus_tage)} value={f.datum} onChange={(e) => setze({ datum: e.target.value })} /></FormField>
        <FormField label="Mahlzeit"><NativeSelect className="w-36" value={f.mahlzeit} onChange={(x) => setze({ mahlzeit: x })} options={Object.keys(MAHLZEIT).map((m) => ({ value: m, label: MAHLZEIT[m] }))} /></FormField>
        <Button variant="outline" disabled={!!busy || !f.datum} onClick={() => vorschau()}>{busy === 'vorschau' && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}Vorschau</Button>
      </div>
      {v && (
        <div className="rounded-md bg-muted/50 p-2.5 space-y-2 text-sm" role="status">
          <p className={v.passt ? '' : 'font-medium'}>{v.hinweis}</p>
          <ul className="text-xs text-muted-foreground space-y-0.5">
            <li>{kcal(v.kcal)}, davon {kcal(v.mehr_kcal)} mehr als die ersetzte Mahlzeit</li>
            {Object.keys(v.am_tag).length > 0 && <li>Am Tag selbst: {Object.entries(v.am_tag).map(([m, n]) => `${MAHLZEIT[m]} ${mitVorzeichen(n)}`).join(' · ')} kcal</li>}
            {v.ausgleich.length > 0 && <li>Ausgleich: {v.ausgleich.map((a) => `${tagText(a.datum)} ${mitVorzeichen(a.kcal)}`).join(' · ')} kcal</li>}
          </ul>
          <div className="flex flex-wrap gap-2">
            {v.passt && <Button size="sm" disabled={!!busy} onClick={einplanen}>{busy === 'anlegen' && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}Einplanen</Button>}
            {v.vorschlaege.map((s) => <Button key={s.text} size="sm" variant="outline" disabled={!!busy} onClick={() => vorschlag(s)}>{s.text}</Button>)}
          </div>
        </div>
      )}
      <p className="text-xs text-muted-foreground">Heute bis {d.voraus_tage} Tage voraus. Es gelten dieselben Regeln wie in der App: am Tag selbst höchstens 30 % leichter, je Ausgleichstag höchstens 10 %, das Eiweißziel bleibt.</p>
    </div>
  );
}

export function GenussKarte({ quelle, schreiben = true, neuLadenBei = '', onGeaendert }) {
  const [d, setD] = useState(null);
  const [datum, setDatum] = useState(null);         // ein Tag der gezeigten Woche; null = die laufende
  const [fehler, setFehler] = useState('');
  const [busy, setBusy] = useState('');
  const laden = useCallback(async () => {
    try { setD(await quelle.woche(datum)); setFehler(''); } catch (e) { setFehler(fehlertext(e, 'Die Woche konnte nicht geladen werden')); }
  }, [quelle, datum]);
  useEffect(() => { laden(); }, [laden, neuLadenBei]);
  const geaendert = () => { laden(); if (onGeaendert) onGeaendert(); };
  const w = d?.woche;
  // Liste: die Genussmahlzeiten der gezeigten Woche, dazu kommende aus anderen Wochen
  const liste = useMemo(() => {
    if (!d?.aktiv) return [];
    const inWoche = w?.genuss || [];
    const ids = new Set(inWoche.map((g) => g.id));
    return [...inWoche, ...(d.kommende || []).filter((g) => !ids.has(g.id))];
  }, [d, w]);
  if (fehler && !d) return <Card><CardContent className="py-6 text-center text-sm text-destructive" role="alert">{fehler}</CardContent></Card>;
  if (!d || !d.aktiv) return null;                  // Schalter aus: Die Karte gibt es nicht
  const loeschen = async (g) => {
    if (!window.confirm(`„${g.name}“ am ${tagText(g.datum)} löschen? Der Ausgleich an den anderen Tagen entfällt.`)) return;
    setBusy(g.id);
    try { await quelle.loeschen(g.id); toast.success('Gelöscht — der Plan steht wieder wie vorher'); geaendert(); }
    catch (e) { toast.error(fehlertext(e)); } finally { setBusy(''); }
  };
  const grenze = async (n) => {
    setBusy('grenze');
    try { await quelle.grenze(Number(n)); toast.success(`Bis zu ${n} Genussmahlzeiten je Woche`); laden(); }
    catch (e) { toast.error(fehlertext(e)); } finally { setBusy(''); }
  };
  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <CardTitle className="text-base flex items-center gap-2"><PartyPopper className="w-4 h-4" aria-hidden="true" />Genuss-Planer</CardTitle>
          {w && (
            <div className="flex items-center gap-1">
              <Button variant="outline" size="icon" className="h-8 w-8 rounded-full" aria-label="Woche davor" onClick={() => setDatum(plusTage(w.von, -7))}><ChevronLeft className="w-4 h-4" /></Button>
              <span className="text-sm tabular-nums px-1">{tagText(w.von, { day: '2-digit', month: '2-digit' })} – {tagText(w.bis, { day: '2-digit', month: '2-digit', year: 'numeric' })}</span>
              <Button variant="outline" size="icon" className="h-8 w-8 rounded-full" aria-label="Woche danach" onClick={() => setDatum(plusTage(w.von, 7))}><ChevronRight className="w-4 h-4" /></Button>
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {d.fehlt && <p className="text-sm text-muted-foreground">{d.fehlt}</p>}
        {w && <><Budget w={w} /><Tage w={w} heute={d.heute} /></>}
        <div className="space-y-1.5">
          <p className="text-sm font-medium">Genussmahlzeiten</p>
          {!liste.length ? <p className="text-sm text-muted-foreground">Noch nichts eingeplant.</p> : (
            <ul className="divide-y rounded-md border">
              {liste.map((g) => (
                <li key={g.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 px-3 py-2 text-sm">
                  <span className="font-medium min-w-0 break-words">{g.symbol ? `${g.symbol} ` : ''}{g.name}</span>
                  <span className="text-xs text-muted-foreground flex-1 min-w-[10rem]">{tagText(g.datum)} · {MAHLZEIT[g.mahlzeit]}{g.portion ? ` · ${PORTION[g.portion]}` : ''} · <span className="tabular-nums">{kcal(g.kcal)}</span>{g.zwei_wochen ? ' · auf zwei Wochen verteilt' : ''}{g.tatsaechlich ? ` · ${TATSAECHLICH[g.tatsaechlich]}` : ''}</span>
                  <Badge variant="outline" className={(STATUS[g.status] || STATUS.geplant).cls}>{(STATUS[g.status] || STATUS.geplant).label}</Badge>
                  {schreiben && g.status !== 'verworfen' && (
                    <Button size="icon" variant="ghost" className="h-8 w-8" aria-label={`${g.name} löschen`} disabled={!!busy} onClick={() => loeschen(g)}>
                      {busy === g.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                    </Button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
        {schreiben && d.coaching_laeuft && !d.fehlt && <Einplanen quelle={quelle} d={d} onFertig={geaendert} />}
        {!d.coaching_laeuft && <p className="text-xs text-muted-foreground">Ohne laufendes Coaching lässt sich nichts einplanen; offene Genussmahlzeiten sind verworfen.</p>}
        {schreiben && d.coaching_laeuft && (
          <div className="flex flex-wrap items-end gap-2">
            <FormField label="Genussmahlzeiten je Woche (dieses Coaching)">
              <NativeSelect className="w-24" value={String(d.max_je_woche)} onChange={grenze} disabled={busy === 'grenze'} options={[0, 1, 2, 3, 4, 5, 6, 7].map((n) => ({ value: String(n), label: String(n) }))} />
            </FormField>
            <p className="text-xs text-muted-foreground pb-2">Standard 2. Gilt für neue Einplanungen.</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/* Schalter für die Leitung (Ernährung › Einstellungen): welche Funktionen die Kunden-App zeigt. Standard: alles aus.
   Schaltbar: Genuss-Planer (Stufe 1), Kommentare (Stufe 2) und das Essensfoto (Stufe 3) — das Essensfoto erst, wenn die
   Leitung den Text der Einwilligung gelesen und freigegeben hat. `schalter` = was hier eingeschaltet ist, `funktionen` =
   was die App anbietet (das Essensfoto nur, wenn zusätzlich die KI-Schätzung aktiv ist). */
const FUNKTION = {
  genuss_planer: ['Genuss-Planer und Wochenbudget', 'Mitglieder im Ernährungscoaching planen Genussmahlzeiten ein (Pizza, Geschäftsessen, Geburtstag); der Server gleicht sie über die Woche aus und portioniert den Plan neu. Coaches sehen Wochenkarte und Genussmahlzeiten am Mitglied.'],
  kommentare: ['Coach-Kommentare zu Mahlzeiten', 'Coaches kommentieren einzelne Tagebuch-Einträge (im CRM am Mitglied und auf der Trainer-Webseite, dort erscheint damit auch das Tagebuch); das Mitglied bekommt einen Push und kann in der App antworten. Ungelesene Antworten zählt das Dashboard.'],
  essensfoto: ['Essensfoto mit Schätzung', 'Mitglieder fotografieren ihr Essen, statt es abzuwiegen — nur mit eigener Einwilligung. Ein KI-Dienst (Anthropic) schätzt Gericht, Portion und Kalorien aus dem Bild; das Mitglied bestätigt oder ändert den Vorschlag. Coaches sehen das Foto am Tagebuch-Eintrag und können die Werte berichtigen.'],
};

function EssensfotoEinstellung({ e, busy, setzen }) {
  const [gelesen, setGelesen] = useState(false);
  const [offen, setOffen] = useState(false);
  const f = e.essensfoto;
  if (!f) return null;
  const ew = f.einwilligung;
  const an = !!e.schalter?.essensfoto;
  return (
    <div className="rounded-md border p-3 space-y-2">
      <p className="text-sm font-medium flex flex-wrap items-center gap-2">Essensfoto: Einwilligung und KI
        {ew.entwurf && <Badge variant="outline" className="border-amber-300 bg-amber-500/10 text-amber-800">Entwurf — nicht anwaltlich geprüft</Badge>}
        <Badge variant="outline">Fassung {ew.version}</Badge></p>
      <p className="text-sm text-muted-foreground">
        {ew.freigegeben
          ? <>Text freigegeben von {ew.freigegeben.von || 'der Leitung'} am {new Date(ew.freigegeben.am).toLocaleDateString('de-DE')}. </>
          : <>Der Text der Einwilligung ist noch nicht freigegeben — so lange lässt sich das Essensfoto nicht einschalten. </>}
        {f.ki.verfuegbar
          ? 'Die KI-Schätzung ist aktiv.'
          : <>{f.ki.grund} {an ? 'Die App bietet das Essensfoto deshalb nicht an; ein hochgeladenes Foto bekäme keinen Vorschlag.' : ''}</>}
      </p>
      <p className="text-xs text-muted-foreground">Höchstens {f.je_tag_max} Fotos je Mitglied und Tag. Nicht bestätigte Fotos werden nach {f.unbestaetigt_stunden} Stunden gelöscht, alle Fotos {f.aufbewahrung_tage} Tage nach dem Ende des Coachings — und sofort, wenn das Mitglied widerruft.</p>
      <Button size="sm" variant="outline" onClick={() => setOffen(!offen)} aria-expanded={offen}>{offen ? 'Text ausblenden' : 'Text der Einwilligung lesen'}</Button>
      {offen && (
        <div className="space-y-2">
          <div className="max-h-72 overflow-y-auto rounded-md bg-muted p-3 text-sm whitespace-pre-line" tabIndex={0} aria-label="Text der Einwilligung">
            <p className="font-medium mb-2">{ew.titel}</p>{ew.text}
          </div>
          {e.darf_schalten && !ew.freigegeben && (
            <div className="space-y-2">
              <label className="flex items-start gap-2 text-sm">
                <input type="checkbox" className="mt-1" checked={gelesen} onChange={(ev) => setGelesen(ev.target.checked)} />
                <span>Ich habe den Text gelesen und gebe ihn in dieser Fassung für die Mitglieder frei. Datenschutzerklärung und Auftragsverarbeitung mit dem KI-Dienst sind geklärt.</span>
              </label>
              <Button size="sm" disabled={busy || !gelesen} onClick={() => setzen({ essensfoto_text_freigeben: true, essensfoto_text_version: ew.version }, 'Text freigegeben — das Essensfoto lässt sich jetzt einschalten')}>Text freigeben</Button>
            </div>
          )}
          {e.darf_schalten && ew.freigegeben && (
            <Button size="sm" variant="outline" disabled={busy} onClick={() => setzen({ essensfoto_text_freigeben: false }, 'Freigabe zurückgenommen — das Essensfoto ist aus')}>Freigabe zurücknehmen</Button>
          )}
        </div>
      )}
    </div>
  );
}

export function FunktionenSchalter() {
  const [e, setE] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { ernaehrungAPI.funktionen().then((r) => setE(r.data)).catch(() => {}); }, []);
  if (!e) return null;
  const setzen = async (daten, meldung) => {
    setBusy(true);
    try { const { data } = await ernaehrungAPI.funktionenSetzen(daten); setE(data); toast.success(meldung); }
    catch (err) { toast.error(fehlertext(err)); } finally { setBusy(false); }
  };
  const schalten = (name, an) => setzen({ [name]: an }, an ? 'Eingeschaltet' : 'Ausgeschaltet — die App blendet die Funktion aus');
  const an = e.schalter || e.funktionen;
  return (
    <Card>
      <CardHeader className="pb-2"><CardTitle className="text-base">Funktionen der Kunden-App</CardTitle></CardHeader>
      <CardContent className="space-y-3">
        <p className="text-sm text-muted-foreground">Was hier aus ist, blendet die App vollständig aus — und das CRM verhält sich wie vorher. Nur die Leitung (Inhaber, Manager) schaltet.</p>
        <ul className="divide-y rounded-md border">
          {Object.keys(FUNKTION).map((name) => {
            const schaltbar = e.schaltbar.includes(name) && (name !== 'essensfoto' || an[name] || !!e.essensfoto?.einwilligung?.freigegeben);
            const nurHier = an[name] && !e.funktionen[name];       // eingeschaltet, aber die App bietet es nicht an (KI aus)
            return (
              <li key={name} className="flex items-start gap-3 p-3">
                <Switch id={`funktion-${name}`} checked={!!an[name]} disabled={busy || !schaltbar || !e.darf_schalten} onCheckedChange={(wert) => schalten(name, wert)} aria-label={FUNKTION[name][0]} />
                <label htmlFor={`funktion-${name}`} className="min-w-0 flex-1">
                  <span className="flex flex-wrap items-center gap-2 text-sm font-medium">{FUNKTION[name][0]}
                    <Badge variant="outline" className={e.funktionen[name] ? 'bg-emerald-500/10 text-emerald-700 border-emerald-200' : nurHier ? 'border-amber-300 bg-amber-500/10 text-amber-800' : ''}>{e.funktionen[name] ? 'aktiv' : nurHier ? 'an — die App zeigt es nicht (KI aus)' : 'aus'}</Badge></span>
                  <span className="block text-sm text-muted-foreground">{FUNKTION[name][1]}</span>
                  {name === 'essensfoto' && !schaltbar && e.darf_schalten && <span className="block text-sm text-muted-foreground">Erst den Text der Einwilligung unten lesen und freigeben.</span>}
                </label>
              </li>
            );
          })}
        </ul>
        <EssensfotoEinstellung e={e} busy={busy} setzen={setzen} />
        {!e.darf_schalten && <p className="text-xs text-muted-foreground">Du siehst den Stand; ändern kann ihn nur die Leitung.</p>}
        {e.geaendert_von && <p className="text-xs text-muted-foreground">Zuletzt geändert von {e.geaendert_von}.</p>}
      </CardContent>
    </Card>
  );
}
